import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import useSWR from 'swr';
import axiosInstance from '@/lib/axios';
import { Button } from '@/components/ui/button';
import { ArrowLeft, CheckCircle2, Clock3, Loader2, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { EventsPageShell } from './components/EventsPageShell';

interface SelfMarkResponse {
  activity: {
    id: string;
    name: string;
    description: string | null;
    selfMarkButtonLabel: string;
    selfMarkRequiresConfirmation: boolean;
  };
  registration: { id: string; name: string; email: string } | null;
  myScan: {
    id: string;
    status: 'PENDING' | 'CONFIRMED';
    scannedAt: string;
    confirmedAt: string | null;
  } | null;
}

export default function ActivitySelfMarkPage() {
  const { communityId, eventId, activityId } = useParams();
  const [submitting, setSubmitting] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const { data, error, isLoading, mutate, isValidating } = useSWR<SelfMarkResponse>(
    eventId && activityId
      ? `/api/events/${eventId}/activities/${activityId}/self-mark`
      : null,
    (url: string) => axiosInstance.get(url).then((res) => res.data),
    { refreshInterval: 4000 }
  );

  const refresh = async () => {
    setIsRefreshing(true);
    try {
      await mutate();
    } finally {
      setIsRefreshing(false);
    }
  };

  const handleSelfMark = async () => {
    setSubmitting(true);
    try {
      const response = await axiosInstance.post(
        `/api/events/${eventId}/activities/${activityId}/self-mark`
      );
      if (response.data.requiresConfirmation) {
        toast.success('Marked — waiting for confirmation');
      } else {
        toast.success('Checked in successfully');
      }
      await mutate();
    } catch (err: unknown) {
      const message =
        (err as { response?: { data?: { error?: string } } })?.response?.data?.error ||
        'Could not mark attendance';
      toast.error(message);
      await mutate();
    } finally {
      setSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex h-[50vh] w-full items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (error || !data?.activity) {
    return (
      <EventsPageShell>
        <div className="mx-auto max-w-md py-16 text-center space-y-4">
          <h1 className="text-xl font-semibold">Self marking unavailable</h1>
          <p className="text-muted-foreground text-sm">
            This activity may not allow self marking, or you may not have access.
          </p>
          <Button asChild variant="outline">
            <Link to={`/${communityId}/events/${eventId}`}>Back to event</Link>
          </Button>
        </div>
      </EventsPageShell>
    );
  }

  const { activity, registration, myScan } = data;
  const buttonLabel = activity.selfMarkButtonLabel || "I'm here";

  return (
    <EventsPageShell>
      <div className="mx-auto max-w-md w-full py-6 space-y-8">
        <div className="flex items-center justify-between gap-2">
          <Link
            to={`/${communityId}/events/${eventId}`}
            className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft className="w-4 h-4 mr-1" />
            Back to event
          </Link>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            aria-label="Refresh"
            disabled={isRefreshing || isValidating}
            onClick={() => void refresh()}
          >
            <RefreshCw
              className={cn('h-4 w-4', (isRefreshing || isValidating) && 'animate-spin')}
            />
          </Button>
        </div>

        <div className="text-center space-y-2">
          <h1 className="text-3xl font-black tracking-tight">{activity.name}</h1>
          {activity.description ? (
            <p className="text-sm text-muted-foreground">{activity.description}</p>
          ) : null}
        </div>

        {!registration ? (
          <div className="rounded-2xl border border-dashed border-border bg-muted/30 p-8 text-center space-y-3">
            <p className="font-medium">You need a registration for this event</p>
            <p className="text-sm text-muted-foreground">
              Register first, then return here to mark yourself present.
            </p>
            <Button asChild>
              <Link to={`/${communityId}/events/${eventId}`}>Go to event</Link>
            </Button>
          </div>
        ) : myScan?.status === 'CONFIRMED' ? (
          <div className="rounded-2xl border border-green-500/30 bg-green-500/10 p-8 text-center space-y-3">
            <CheckCircle2 className="h-10 w-10 text-green-600 mx-auto" />
            <p className="font-semibold text-lg">You&apos;re checked in</p>
            <p className="text-sm text-muted-foreground">
              Your attendance for {activity.name} is confirmed.
            </p>
          </div>
        ) : myScan?.status === 'PENDING' ? (
          <div className="rounded-2xl border border-amber-500/30 bg-amber-500/10 p-8 text-center space-y-3">
            <Clock3 className="h-10 w-10 text-amber-600 mx-auto" />
            <p className="font-semibold text-lg">Waiting for confirmation</p>
            <p className="text-sm text-muted-foreground">
              A scanner manager will confirm your check-in shortly.
            </p>
          </div>
        ) : (
          <div className="rounded-2xl border border-border bg-card p-8 text-center space-y-6 shadow-sm">
            <div className="space-y-1">
              <p className="font-medium">{registration.name}</p>
              <p className="text-sm text-muted-foreground">{registration.email}</p>
            </div>
            <Button
              size="lg"
              className="w-full h-14 text-lg font-semibold rounded-2xl"
              disabled={submitting}
              onClick={() => void handleSelfMark()}
            >
              {submitting ? <Loader2 className="h-5 w-5 animate-spin" /> : buttonLabel}
            </Button>
            {activity.selfMarkRequiresConfirmation ? (
              <p className="text-xs text-muted-foreground">
                After you tap, a manager will confirm your check-in on the scanner.
              </p>
            ) : (
              <p className="text-xs text-muted-foreground">
                Your check-in will be confirmed immediately.
              </p>
            )}
          </div>
        )}
      </div>
    </EventsPageShell>
  );
}
